/**
 * Host portal → Registrations: who has registered for a Connect program.
 *
 * Program picker (defaults to the current program), headline counts, the
 * uni / how-did-you-hear breakdowns, search + status filter, a detail dialog
 * with every answer, withdraw / restore, and a CSV download. Data:
 * GET /api/admin/connect/registrations (all statuses, filtered here so search
 * is instant) and the program's questions from GET /api/forms/:formKey.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ClipboardList, Download, RefreshCw, Search } from "lucide-react";
import { apiClient, formatEventDates, type FormField } from "@alight/contracts";
import {
  Badge,
  Banner,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@alight/ui";
import { formatDate, formatDay } from "../../components/admin-bits";
import {
  ApiError,
  adminDownloadRegistrationsCsv,
  adminListRegistrations,
  adminSetRegistrationStatus,
  type AdminRegistrationsResult,
  type AdminStatusFilter,
  type Registration,
} from "../../lib/registrations";

export const Route = createFileRoute("/admin/registrations")({
  component: AdminRegistrations,
  /** `?ref=CON-XXXXXX` — the deep link in the "someone registered" email.
   *  It pre-fills the search and opens that person's details. */
  validateSearch: (s: Record<string, unknown>): { ref?: string } =>
    typeof s.ref === "string" && s.ref.trim() !== "" ? { ref: s.ref.trim() } : {},
});

const api = apiClient("");

const TABS: Array<{ key: AdminStatusFilter; label: string }> = [
  { key: "registered", label: "Registered" },
  { key: "withdrawn", label: "Withdrawn" },
  { key: "all", label: "All" },
];

/** "Other — ANU" when the select is Other and its follow-up has an answer. */
function answerText(reg: Registration, name: string): string {
  const v = reg.answers[name] ?? "";
  const other = reg.answers[`${name}_other`];
  return v === "Other" && other ? `Other — ${other}` : v;
}

/** The Location filter's value for "didn't answer" (blank is not a valid
 *  SelectItem value). */
const NO_LOCATION = "__none";

function matchesLocation(reg: Registration, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === NO_LOCATION) return !reg.location;
  return reg.location === filter;
}

function matches(reg: Registration, q: string): boolean {
  if (!q) return true;
  const hay = `${reg.first_name} ${reg.last_name} ${reg.email} ${reg.location} ${reg.answers.school ?? ""}`.toLowerCase();
  return hay.includes(q);
}

function AdminRegistrations() {
  const [programId, setProgramId] = useState<number | undefined>(undefined);
  const [data, setData] = useState<AdminRegistrationsResult | null>(null);
  const [fields, setFields] = useState<FormField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<AdminStatusFilter>("registered");
  // A deep link from the notification email arrives as ?ref=CON-XXXXXX: the
  // search starts on that reference so the row is already isolated.
  const { ref: deepLinkRef } = Route.useSearch();
  const [query, setQuery] = useState(deepLinkRef ?? "");
  const [openedDeepLink, setOpenedDeepLink] = useState(false);
  const [locationFilter, setLocationFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async (pid?: number) => {
    setLoading(true);
    setError(null);
    try {
      setData(await adminListRegistrations({ program_id: pid, status: "all" }));
    } catch (err) {
      setData(null);
      setError(err instanceof ApiError ? err.message : "Couldn't load registrations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(programId);
  }, [programId, load]);

  const formKey = data?.program?.form_key;
  useEffect(() => {
    if (!formKey) {
      setFields([]);
      return;
    }
    let live = true;
    api
      .getForm(formKey)
      .then((f) => live && setFields(f))
      .catch(() => live && setFields([]));
    return () => {
      live = false;
    };
  }, [formKey]);

  const program = data?.program ?? null;
  const all = data?.registrations ?? [];
  const registered = useMemo(() => all.filter((r) => r.status === "registered"), [all]);
  const q = query.trim().toLowerCase();
  const rows = all.filter(
    (r) => (tab === "all" || r.status === tab) && matchesLocation(r, locationFilter) && matches(r, q),
  );
  const locationValues = [...new Set(all.map((r) => r.location).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
  const someWithoutLocation = all.some((r) => !r.location);
  const filtered = Boolean(q) || locationFilter !== "all";
  // Open the linked registration once, when the list first arrives. Guarded so
  // closing the dialog does not immediately reopen it.
  useEffect(() => {
    if (openedDeepLink || !deepLinkRef || all.length === 0) return;
    const match = all.find((r) => r.ref.toLowerCase() === deepLinkRef.toLowerCase());
    setOpenedDeepLink(true);
    if (match) setSelectedId(match.id);
  }, [openedDeepLink, deepLinkRef, all]);

  const selected = all.find((r) => r.id === selectedId) ?? null;
  const fieldByName = new Map(fields.map((f) => [f.name, f]));

  function replaceRegistration(updated: Registration) {
    setData((prev) => {
      if (!prev) return prev;
      const registrations = prev.registrations.map((r) => (r.id === updated.id ? updated : r));
      return {
        ...prev,
        registrations,
        counts: {
          registered: registrations.filter((r) => r.status === "registered").length,
          withdrawn: registrations.filter((r) => r.status === "withdrawn").length,
        },
      };
    });
  }

  async function download() {
    if (!program) return;
    setDownloading(true);
    try {
      await adminDownloadRegistrationsCsv(program.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't download the CSV.");
    } finally {
      setDownloading(false);
    }
  }

  const dates = program ? formatEventDates(program.start_date, program.end_date) : "";

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold text-ink">Registrations</h1>
          <p className="mt-1 text-sm text-muted">Everyone who has registered through the website.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => void load(programId)} disabled={loading}>
            <RefreshCw className={loading ? "h-4 w-4 animate-spin" : "h-4 w-4"} aria-hidden />
            Refresh
          </Button>
          <Button type="button" size="sm" onClick={() => void download()} disabled={!program || downloading}>
            <Download className="h-4 w-4" aria-hidden />
            {downloading ? "Preparing…" : "Download CSV"}
          </Button>
        </div>
      </header>

      {(data?.programs.length ?? 0) > 0 && (
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full space-y-1.5 sm:w-80">
            <Label htmlFor="reg-program">Program</Label>
            <Select
              value={program ? String(program.id) : ""}
              onValueChange={(v) => {
                setSelectedId(null);
                setProgramId(Number(v));
              }}
            >
              <SelectTrigger id="reg-program" className="w-full">
                <SelectValue placeholder="Choose a program" />
              </SelectTrigger>
              <SelectContent>
                {data?.programs.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.title}
                    {p.active ? "" : " (inactive)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {dates && <p className="pb-2 text-sm text-muted">{dates}</p>}
        </div>
      )}

      {error && (
        <div role="alert" className="rounded-card border border-accent/40 bg-accent/10 px-4 py-3 text-sm text-ink">
          {error}{" "}
          <button
            type="button"
            onClick={() => void load(programId)}
            className="font-semibold text-primary underline-offset-2 hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {loading && !data && (
        <div className="rounded-card border border-border bg-card px-4 py-10 text-center text-sm text-muted">
          Loading registrations…
        </div>
      )}

      {!loading && !error && data && !program && (
        <div className="rounded-card border border-border bg-card px-4 py-12 text-center">
          <ClipboardList className="mx-auto h-8 w-8 text-muted/60" aria-hidden />
          <p className="mt-3 text-sm text-muted">No programs yet — create one under Programs to take registrations.</p>
        </div>
      )}

      {data && program && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Registered" value={data.counts.registered} tone="primary" />
            <Stat label="Withdrawn" value={data.counts.withdrawn} />
            <Stat label="Total" value={data.counts.registered + data.counts.withdrawn} />
          </div>

          {registered.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <Breakdown title="Location" values={registered.map((r) => r.location || "Not answered")} />
              {["uni", "heard_from"].map((name) => {
                const f = fieldByName.get(name);
                if (!f) return null;
                return (
                  <Breakdown
                    key={name}
                    title={f.label}
                    values={registered.map((r) => r.answers[f.name] || "Not answered")}
                  />
                );
              })}
            </div>
          )}

          <div className="flex flex-col gap-3 border-b border-border sm:flex-row sm:items-end sm:justify-between">
            <div role="tablist" aria-label="Registration status" className="flex gap-2">
              {TABS.map((t) => {
                const n =
                  t.key === "all" ? data.counts.registered + data.counts.withdrawn : data.counts[t.key];
                return (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.key}
                    onClick={() => setTab(t.key)}
                    className={
                      "-mb-px border-b-2 px-3 pb-2 text-sm font-semibold transition-colors " +
                      (tab === t.key ? "border-primary text-primary" : "border-transparent text-muted hover:text-ink")
                    }
                  >
                    {t.label} <span className="font-normal">({n})</span>
                  </button>
                );
              })}
            </div>
            <div className="mb-2 flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              {(locationValues.length > 0 || someWithoutLocation) && (
                <Select value={locationFilter} onValueChange={setLocationFilter}>
                  <SelectTrigger aria-label="Filter by location" className="w-full sm:w-52">
                    <SelectValue placeholder="All locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All locations</SelectItem>
                    {locationValues.map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                    {someWithoutLocation && <SelectItem value={NO_LOCATION}>Not answered</SelectItem>}
                  </SelectContent>
                </Select>
              )}
              <div className="relative w-full sm:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden />
                <Input
                  type="search"
                  aria-label="Search registrations"
                  placeholder="Search name, email, location or school"
                  className="pl-9"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          {rows.length === 0 ? (
            <div className="rounded-card border border-border bg-card px-4 py-12 text-center">
              <ClipboardList className="mx-auto h-8 w-8 text-muted/60" aria-hidden />
              <p className="mt-3 text-sm text-muted">
                {filtered
                  ? "No registrations match that search."
                  : all.length === 0
                    ? "No registrations yet. They'll appear here as people register on the website."
                    : tab === "withdrawn"
                      ? "Nobody has withdrawn."
                      : "Nothing to show."}
              </p>
            </div>
          ) : (
            <Card>
              <CardContent className="overflow-x-auto p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>School</TableHead>
                      <TableHead>Uni</TableHead>
                      <TableHead>Heard from</TableHead>
                      <TableHead>Registered</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id} className="cursor-pointer" onClick={() => setSelectedId(r.id)}>
                        <TableCell className="whitespace-nowrap">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedId(r.id);
                            }}
                            className="font-medium text-ink hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          >
                            {r.first_name} {r.last_name}
                          </button>
                        </TableCell>
                        <TableCell className="max-w-[11rem] truncate text-muted" title={r.email}>{r.email}</TableCell>
                        <TableCell className="max-w-[12rem] truncate" title={r.location}>
                          {r.location || "—"}
                        </TableCell>
                        <TableCell className="max-w-[12rem] truncate">{r.answers.school ?? ""}</TableCell>
                        <TableCell className="max-w-[12rem] truncate">{answerText(r, "uni")}</TableCell>
                        <TableCell className="max-w-[12rem] truncate">{answerText(r, "heard_from")}</TableCell>
                        <TableCell className="whitespace-nowrap text-muted">{formatDay(r.created_at)}</TableCell>
                        <TableCell>
                          {r.status === "registered" ? (
                            <Badge variant="success">Registered</Badge>
                          ) : (
                            <Badge variant="muted">Withdrawn</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <RegistrationDialog
        registration={selected}
        fields={fields}
        onClose={() => setSelectedId(null)}
        onChanged={replaceRegistration}
      />
    </section>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "primary" }) {
  return (
    <div className="rounded-card border border-border bg-card p-4">
      <p className="text-xs font-bold uppercase tracking-wide text-muted">{label}</p>
      <p className={`mt-1 font-heading text-3xl font-extrabold ${tone === "primary" ? "text-primary" : "text-ink"}`}>
        {value}
      </p>
    </div>
  );
}

/** One "how did this split?" card — a labelled count + bar per distinct value. */
function Breakdown({ title, values }: { title: string; values: string[] }) {
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  const total = values.length;
  const entries = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return (
    <Card>
      <CardContent className="p-5">
        <h2 className="font-heading text-base font-bold text-ink">{title}</h2>
        <ul className="mt-4 space-y-3">
          {entries.map(([label, n]) => {
            const pct = total ? Math.round((n / total) * 100) : 0;
            return (
              <li key={label}>
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate text-ink" title={label}>
                    {label}
                  </span>
                  <span className="shrink-0 tabular-nums text-muted">
                    {n} · {pct}%
                  </span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-band" aria-hidden>
                  <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

function RegistrationDialog({
  registration,
  fields,
  onClose,
  onChanged,
}: {
  registration: Registration | null;
  fields: FormField[];
  onClose: () => void;
  onChanged: (r: Registration) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setError(null), [registration?.id]);

  if (!registration) return null;
  const r = registration;
  const withdrawn = r.status === "withdrawn";
  const known = new Set(fields.map((f) => f.name));
  const rows: Array<[string, string]> = [
    ["Email", r.email],
    ["Mobile", r.phone || "—"],
    ["Location", r.location || "—"],
    ...fields
      // An "If other…" follow-up only matters when it was answered.
      .filter((f) => !(f.name.endsWith("_other") && !r.answers[f.name]))
      .map((f): [string, string] => [f.label, r.answers[f.name] ? (f.type === "checkbox" ? "Yes" : r.answers[f.name]!) : "—"]),
    // Answers to questions since removed from the form are still shown.
    ...Object.entries(r.answers)
      .filter(([k]) => !known.has(k))
      .map(([k, v]): [string, string] => [k, v]),
    ["Reference", r.ref],
    ["Registered", formatDate(r.created_at)],
    ["Last updated", formatDate(r.updated_at)],
  ];

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      const { registration: updated } = await adminSetRegistrationStatus(r.id, withdrawn ? "registered" : "withdrawn");
      onChanged(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't update the registration.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="pr-6 font-heading text-xl">
            {r.first_name} {r.last_name}
          </DialogTitle>
          <DialogDescription>
            {withdrawn ? <Badge variant="muted">Withdrawn</Badge> : <Badge variant="success">Registered</Badge>}
          </DialogDescription>
        </DialogHeader>
        <dl className="divide-y divide-border rounded-card border border-border text-sm">
          {rows.map(([label, value], i) => (
            <div key={`${label}-${i}`} className="grid gap-0.5 px-3 py-2 sm:grid-cols-[10rem_1fr] sm:gap-3">
              <dt className="font-medium text-muted">{label}</dt>
              <dd className="break-words text-ink">{value}</dd>
            </div>
          ))}
        </dl>
        {error && <Banner variant="error">{error}</Banner>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button type="button" variant={withdrawn ? "default" : "accent"} onClick={() => void toggle()} disabled={busy}>
            {busy ? "Saving…" : withdrawn ? "Restore registration" : "Mark as withdrawn"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
